/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { useState, useEffect, useContext, Fragment } from 'react'
import {
  Input as LCInput,
  Button,
  notificationConnect,
} from '@livechat/design-system'

import api from '../api'
import { Context } from '../context'
import SurveyLabelsSelect from './SurveyLabelsSelect'

const formatNotification = (
  content,
  variant = 'error',
  autoHideDelayTime = 4000,
  removable = true,
  horizontalPosition = 'center',
  verticalPosition = 'top'
) => ({
  type: 'toast',
  autoHideDelayTime,
  payload: {
    variant,
    content,
    horizontalPosition,
    verticalPosition,
    removable,
  },
})

const wrapperCss = css`
  width: 100%;
  margin-bottom: 10px;
`

const containerCss = css`
  display: flex;
`

const inputCss = css`
  width: 100%;
  height: 40px;
`

const errorCss = css`
  font-size: 12px;
  color: #d64646;
  margin-top: 5px;
`

const btnCss = css`
  height: 40px;
  width: 100px;
  margin-left: 10px;
`

const labelCss = css`
  display: inline-block;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 300;
`

export const formatItem = (value) => ({
  key: value,
  props: { name: value, value: value },
})

const SurveyLabels = ({ survey, notificationSystem }) => {
  const {
    surveyToken,
    settings,
    setSettings,
    assignLabelToSurvey,
    removeLabelFromSurvey,
  } = useContext(Context)
  const [value, setValue] = useState('')
  const [error, setError] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const surveyGroupingLabels = survey?.grouping_labels || []
  const [activeSurveyGroupingLabels, setActiveSurveyGroupingLabels] = useState(
    surveyGroupingLabels?.map((l) => ({
      key: l,
      props: { name: l, value: l },
    })) || []
  )
  useEffect(() => {
    setIsDirty(!!value)
  }, [value])

  const addNewLabel = async () => {
    setIsLoading(true)
    try {
      if (settings?.grouping_labels?.includes(value)) {
        setError('Label already exists in your license. Use the existing one.')
        setIsLoading(false)
        return
      }

      await api.addNewGroupingLabel(surveyToken, survey.id, {
        grouping_label: value,
      })
      notificationSystem.add(
        formatNotification('New grouping label added successfully!', 'success')
      )

      const newGroupingLabels =
        settings?.grouping_labels?.length > 0
          ? [...settings.grouping_labels, value]
          : [value]

      setSettings({
        ...settings,
        grouping_labels: newGroupingLabels,
      })

      assignLabelToSurvey(survey.id, value)

      setActiveSurveyGroupingLabels([
        ...activeSurveyGroupingLabels,
        formatItem(value),
      ])
      setValue('')
      setError()
    } catch (err) {
      console.log(err)
      notificationSystem.add(formatNotification(error?.message))
    }
    setIsLoading()
    setError()
  }

  const onAssignLabelToSurvey = async (label) => {
    try {
      await api.assignLabelToSurvey(surveyToken, survey.id, {
        grouping_label: label,
      })
      assignLabelToSurvey(survey.id, label)
      notificationSystem.add(
        formatNotification(
          'Grouping label assigned to survey successfully!',
          'success'
        )
      )
    } catch (err) {
      console.log(err)
      notificationSystem.add(formatNotification(error?.message))
    }
  }

  const onRemoveLabelFromSurvey = async (label) => {
    try {
      await api.removeLabelFromSurvey(surveyToken, survey.id, {
        grouping_label: label,
      })
      removeLabelFromSurvey(survey.id, label)
      notificationSystem.add(
        formatNotification(
          'Grouping label removed from survey successfully!',
          'success'
        )
      )
    } catch (err) {
      console.log(err)
      notificationSystem.add(formatNotification(error?.message))
    }
  }

  return (
    <div>
      <div css={wrapperCss}>
        <span css={labelCss}>Create new label</span>
        <div css={containerCss}>
          <LCInput
            css={inputCss}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={error}
            placeholder="Type new label name"
          />
          <Button
            onClick={addNewLabel}
            css={btnCss}
            loading={isLoading}
            disabled={!isDirty}
          >
            + Add
          </Button>
        </div>
        {error && <span css={errorCss}>{error}</span>}
      </div>
      {settings.grouping_labels?.length > 0 && (
        <Fragment>
          <span css={labelCss}>or use existing one</span>
          <SurveyLabelsSelect
            licenseGroupingLabels={
              settings?.grouping_labels?.map((l) => formatItem(l)) || []
            }
            selectedItems={activeSurveyGroupingLabels}
            setSelectedItems={setActiveSurveyGroupingLabels}
            assignLabelToSurvey={onAssignLabelToSurvey}
            removeLabelFromSurvey={onRemoveLabelFromSurvey}
          />
        </Fragment>
      )}
    </div>
  )
}

export default notificationConnect(SurveyLabels)
